<div class="space-y-6">
  @if (loadError()) {
    <div class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300" role="alert">
      {{ loadError() }}
    </div>
  }

  <!-- Team -->
  <section class="us-panel" aria-labelledby="us-team-title">
    <div class="us-panel__head">
      <div>
        <h2 id="us-team-title" class="us-title">Tu equipo</h2>
        <p class="us-sub">
          @if (!loading()) {
            {{ users().length }} persona{{ users().length !== 1 ? 's' : '' }} con acceso a tu negocio
          } @else {
            Cargando…
          }
        </p>
      </div>
      @if (canInvite() && !formOpen()) {
        <button type="button" class="us-btn us-btn--primary" (click)="openForm()">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Invitar
        </button>
      }
    </div>

    @if (loading()) {
      <ul class="us-list" aria-hidden="true">
        @for (i of [1, 2, 3]; track i) {
          <li class="us-row"><span class="us-avatar us-skeleton"></span><span class="us-skeleton h-4 w-40 rounded"></span></li>
        }
      </ul>
    } @else {
      <ul class="us-list">
        @for (user of users(); track user.userId) {
          <li class="us-row">
            <span class="us-avatar">
              @if (user.avatarUrl) {
                <img [src]="user.avatarUrl" alt="" class="h-full w-full object-cover" loading="lazy" />
              } @else {
                {{ initials(user) | uppercase }}
              }
            </span>
            <div class="min-w-0 flex-1">
              <p class="us-name truncate">
                {{ user.firstName }} {{ user.lastName }}
                @if (user.isCurrentUser) { <span class="us-you">Tú</span> }
              </p>
              <p class="us-meta truncate">{{ user.email || 'Sin correo' }}</p>
            </div>
            <div class="us-row__roles">
              @for (role of user.roles; track role.roleId) {
                <span class="us-role" [attr.data-role]="role.code">{{ role.name }}</span>
              }
            </div>
            <span class="us-access" [class.us-access--on]="user.hasAccount"
              [title]="user.hasAccount ? 'Tiene correo y contraseña para entrar' : 'Aún no tiene usuario para entrar'">
              <span class="us-dot"></span>
              {{ user.hasAccount ? 'Puede entrar' : 'Sin usuario' }}
            </span>
          </li>
        }
      </ul>
    }
  </section>

  <!-- Invite form -->
  @if (formOpen()) {
    <section class="us-panel" aria-labelledby="us-invite-title">
      <div class="us-panel__head">
        <div>
          <h2 id="us-invite-title" class="us-title">Invitar a alguien</h2>
          <p class="us-sub">Le llega un enlace para completar sus datos y crear su contraseña. Vence en 7 días.</p>
        </div>
        <button type="button" class="us-icon-btn" (click)="closeForm()" aria-label="Cerrar">
          <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      @if (result(); as r) {
        <!-- Sent -->
        <div class="us-result" [class.us-result--warn]="!r.invitation.sent" role="status">
          @if (r.invitation.sent) {
            <p class="us-result__title">Invitación enviada a {{ r.name }}</p>
            <p class="us-meta">
              Le escribimos por {{ r.invitation.channel === 'whatsapp' ? 'WhatsApp' : 'correo' }} a {{ r.target }}.
              Si no le llega, compártele el enlace.
            </p>
          } @else {
            <p class="us-result__title">Creamos la invitación, pero no se pudo enviar</p>
            <p class="us-meta">{{ r.invitation.deliveryError }} Compártele el enlace tú mismo.</p>
          }
          <div class="us-link">
            <code class="truncate">{{ link(r.invitation.slug) }}</code>
            <button type="button" class="us-btn us-btn--ghost" (click)="copyLink(r.invitation.slug)">
              {{ copiedSlug() === r.invitation.slug ? 'Copiado' : 'Copiar' }}
            </button>
            <a class="us-btn us-btn--ghost" [href]="resultShareUrl()" target="_blank" rel="noopener">Enviar por WhatsApp</a>
          </div>
          <div class="mt-4 flex gap-2">
            <button type="button" class="us-btn us-btn--primary" (click)="openForm()">Invitar a otra persona</button>
            <button type="button" class="us-btn us-btn--ghost" (click)="closeForm()">Listo</button>
          </div>
        </div>
      } @else {
        <form [formGroup]="form" (ngSubmit)="submit()" class="space-y-6" novalidate>
          <!-- Role -->
          <fieldset>
            <legend class="us-label">Rol</legend>
            <div class="us-roles" role="radiogroup">
              @for (role of roles(); track role.roleId) {
                <button type="button" class="us-role-card" role="radio"
                  [attr.aria-checked]="form.controls.roleId.value === role.roleId" (click)="selectRole(role)">
                  <span class="us-role-card__name">{{ role.name }}</span>
                  <span class="us-role-card__hint">{{ roleHint(role.code) }}</span>
                </button>
              }
            </div>
          </fieldset>

          <!-- Professional link -->
          @if (isPractitioner()) {
            <div>
              <label class="us-label" for="us-professional">¿Qué profesional es?</label>
              @if (professionalsWithoutUser().length) {
                <select id="us-professional" class="us-input" formControlName="professionalUserId">
                  <option value="" disabled>Elige un profesional…</option>
                  @for (p of professionalsWithoutUser(); track p.userId) {
                    <option [value]="p.userId">
                      {{ professionalName(p) }}{{ p.hasPendingInvitation ? ' (ya invitado)' : '' }}
                    </option>
                  }
                </select>
                <p class="us-help">Su usuario queda unido a este profesional: verá su agenda y ocupa su cupo, no uno nuevo.</p>
              } @else {
                <p class="us-help">
                  Todos tus profesionales ya tienen usuario. Para invitar a uno nuevo, primero créalo en
                  <a class="us-a" routerLink="/settings/professionals">Profesionales</a>.
                </p>
              }
            </div>
          }

          <!-- Person -->
          <div class="grid gap-4 sm:grid-cols-2">
            <div>
              <label class="us-label" for="us-first">Nombre</label>
              <input id="us-first" class="us-input" formControlName="firstName" autocomplete="off" placeholder="Ana" [class.us-input--error]="invalid('firstName')" />
            </div>
            <div>
              <label class="us-label" for="us-last">Apellido</label>
              <input id="us-last" class="us-input" formControlName="lastName" autocomplete="off" placeholder="Pérez" [class.us-input--error]="invalid('lastName')" />
            </div>
          </div>
          <div>
            <label class="us-label" for="us-email">Correo</label>
            <input id="us-email" type="email" class="us-input" formControlName="email" autocomplete="off" placeholder="ana@correo.com" [class.us-input--error]="invalid('email')" />
            <p class="us-help">Con este correo va a iniciar sesión.</p>
          </div>

          <!-- Channel -->
          <fieldset>
            <legend class="us-label">Enviar la invitación por</legend>
            <div class="us-segmented" role="radiogroup">
              <button type="button" class="us-segmented__item" role="radio"
                [attr.aria-checked]="form.controls.channel.value === 'whatsapp'" (click)="selectChannel('whatsapp')">WhatsApp</button>
              <button type="button" class="us-segmented__item" role="radio"
                [attr.aria-checked]="form.controls.channel.value === 'email'" (click)="selectChannel('email')">Correo</button>
            </div>
          </fieldset>

          <div class="grid gap-4 sm:grid-cols-[minmax(0,14rem)_1fr]">
            <div>
              <label class="us-label" for="us-country">País</label>
              <select id="us-country" class="us-input" formControlName="countryId" [class.us-input--error]="invalid('countryId')">
                <option value="" disabled>País…</option>
                @for (c of countries(); track c.countryId) {
                  <option [value]="c.countryId">{{ c.dialCode }} {{ c.name }}</option>
                }
              </select>
            </div>
            <div>
              <label class="us-label" for="us-phone">
                Celular @if (form.controls.channel.value === 'email') { <span class="us-optional">(opcional)</span> }
              </label>
              <input id="us-phone" class="us-input" formControlName="phone" inputmode="numeric" autocomplete="off"
                [mask]="selectedCountry()?.phoneMask ?? ''" [attr.placeholder]="selectedCountry()?.phoneHolder ?? ''"
                [class.us-input--error]="invalid('phone')" />
            </div>
          </div>

          @if (formError()) {
            <p class="us-error" role="alert">{{ formError() }}</p>
          }

          <div class="flex flex-wrap justify-end gap-2">
            <button type="button" class="us-btn us-btn--ghost" (click)="closeForm()">Cancelar</button>
            <button type="submit" class="us-btn us-btn--primary" [disabled]="sending()">
              {{ sending() ? 'Enviando…' : 'Enviar invitación' }}
            </button>
          </div>
        </form>
      }
    </section>
  }

  <!-- Pending invitations -->
  @if (pendingInvitations().length || expiredInvitations().length) {
    <section class="us-panel" aria-labelledby="us-pending-title">
      <div class="us-panel__head">
        <div>
          <h2 id="us-pending-title" class="us-title">Invitaciones</h2>
          <p class="us-sub">Personas que todavía no han creado su contraseña</p>
        </div>
      </div>
      <ul class="us-list">
        @for (inv of invitations(); track inv.userInvitationId) {
          <li class="us-row us-row--wrap">
            <span class="us-avatar us-avatar--ghost">{{ initials(inv) | uppercase }}</span>
            <div class="min-w-0 flex-1">
              <p class="us-name truncate">{{ inv.firstName }} {{ inv.lastName }}</p>
              <p class="us-meta truncate">
                {{ inv.role.name }} ·
                {{ inv.channel === 'whatsapp' ? 'por WhatsApp' : 'por correo' }} ·
                @if (inv.status === 'expired') {
                  <span class="us-expired">venció el {{ inv.expiresAt | date: 'd MMM' }}</span>
                } @else {
                  vence el {{ inv.expiresAt | date: 'd MMM' }}
                }
              </p>
            </div>
            @if (canInvite()) {
              <div class="us-row__actions">
                @if (inv.status === 'pending') {
                  <button type="button" class="us-btn us-btn--ghost us-btn--sm" (click)="copyLink(inv.slug)">
                    {{ copiedSlug() === inv.slug ? 'Copiado' : 'Copiar enlace' }}
                  </button>
                  <a class="us-btn us-btn--ghost us-btn--sm" [href]="whatsAppShareUrl(inv)" target="_blank" rel="noopener">WhatsApp</a>
                } @else {
                  <button type="button" class="us-btn us-btn--ghost us-btn--sm"
                    (click)="openForm(inv.professionalUserId ?? null)">Invitar de nuevo</button>
                }
                <button type="button" class="us-icon-btn us-icon-btn--danger" (click)="askRevoke(inv)"
                  [attr.aria-label]="'Cancelar la invitación de ' + inv.firstName" title="Cancelar invitación">
                  <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            }
          </li>
        }
      </ul>
    </section>
  }
</div>

<app-confirm-modal [open]="!!toRevoke()" (openChange)="!$event && toRevoke.set(null)"
  title="Cancelar invitación"
  [subtitle]="toRevoke() ? 'El enlace de ' + toRevoke()!.firstName + ' dejará de funcionar.' : ''"
  cancelText="Volver" confirmText="Cancelar invitación"
  variant="danger" (confirm)="confirmRevoke()" (cancel)="toRevoke.set(null)">
</app-confirm-modal>
